import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SmlsModal, SmlsButton, SmlsInputToken, SmlsAnimation } from '@smiles/smiles-ui-kit-react';
import { ModalHeader, ModalBody } from 'reactstrap';
import { generateTokenSms, validateTokenSms, getConstants } from 'services/core/services';
import { useHistory, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Config } from 'services/core/config';
import DefaultMsgPage from 'components/DefaultMsgPage';
import { loginDataLayer } from 'utils/dataLayer';

type HistoryState = {
  maskedPhone?: string;
  phoneNumber?: string;
  maskedEmail?: string;
  pushEmail?: string;
};

const Phone: React.FC = () => {
  const TOKEN_NAME = process.env.REACT_APP_TOKEN_NAME || 'session-token';
  const { t } = useTranslation('phone');
  const history = useHistory();
  const location = useLocation();
  const [phoneIsOpen, setPhoneIsOpen] = useState(false);
  const [insertPhoneTokenIsOpen, setInsertPhoneToken] = useState(false);
  const [genericError, setGenericError] = useState(false);
  const [errorToken, setErrorToken] = useState('');
  const [smsToken, setSmsToken] = useState('');
  const [expiredToken, setExpiredToken] = useState(false);
  const [isLoadingAnimation, setIsLoadingAnimation] = useState({ isSuccess: true, isLoading: false });
  const [animationMessage, setAnimationMessage] = useState({
    loading: [t('wait'), t('validating')],
    success: [t('validate_success')],
    error: [],
  });
  const [onFinishLoading, setOnFinishLoading] = useState<() => any>();
  const [timeLeft, setTimeLeft] = useState(window.smlsTokenTime ? window.smlsTokenTime + 3 : 33);
  const [phone, setPhone] = useState('');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [flipFlopClear, setFlipFlopClear] = useState<boolean>(false);
  const [hasToEnableRecaptcha, setHasToEnableRecaptcha] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string | undefined>(undefined);
  const [isValidRecaptcha, setIsValidRecaptcha] = useState(false);

  const formatTime = (sec: number) => {
    const pad = (num: number) => (num >= 10 ? num.toString() : `0${num}`);
    const mm = pad(Math.floor(sec / 60));
    const ss = pad(sec % 60);

    return `${mm}:${ss}`;
  };

  useEffect(() => {
    const checkEnableRecaptcha = localStorage.getItem('@smls-validate-actions:enableRecaptcha');
    if (checkEnableRecaptcha === 'true') {
      setHasToEnableRecaptcha(true);
    }
  }, [localStorage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft((prev) => {
        if (prev - 1 < 0) {
          setExpiredToken(true);
          return prev;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPhoneIsOpen(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleToggleInsertPhoneToken = async () => {
    setAnimationMessage({
      loading: [t('wait'), t('validating')],
      success: [t('token_send')],
      error: [],
    });

    setIsLoadingAnimation({ isSuccess: true, isLoading: true });
    const token = localStorage.getItem(TOKEN_NAME) || '';
    Config.saveTokenAuthUser({ accessToken: token });
    try {
      const { success, data } = await generateTokenSms(phone, recaptchaValue);
      if (data.enableRecaptcha) {
        setHasToEnableRecaptcha(true);
        localStorage.setItem('@smls-validate-actions:enableRecaptcha', 'true');
      }

      if (!success) {
        throw new Error();
      }
    } catch {
      setGenericError(true);
      setIsLoadingAnimation({ isSuccess: false, isLoading: false });
      return;
    }
    setAnimationMessage({
      loading: [t('wait'), t('validating')],
      success: [t('token_send')],
      error: [],
    });
    setFlipFlopClear((prev) => !prev);
    setOnFinishLoading(() => {
      setSmsToken('');
      setErrorToken('');
      setInsertPhoneToken(true);
      setExpiredToken(false);
      setTimeLeft(window.smlsTokenTime ? window.smlsTokenTime + 3 : 33);
      setRecaptchaValue('');
      setIsValidRecaptcha(false);
    });
    setIsLoadingAnimation({ isSuccess: true, isLoading: false });
  };

  const handleValidateToken = async () => {
    setAnimationMessage({
      loading: [t('wait'), t('validating')],
      success: [t('validate_success')],
      error: [],
    });
    setIsLoadingAnimation({ isSuccess: true, isLoading: true });

    try {
      const resp = await validateTokenSms(smsToken);
      setOnFinishLoading(() => {
        if (!resp.success && resp.errors.flow.tokenExpired) {
          setIsLoadingAnimation({ isSuccess: !!resp.success, isLoading: false });
          setErrorToken(t('invalid_token'));
          setTimeLeft(0);
          return;
        }

        if (!resp.success) {
          setIsLoadingAnimation({ isSuccess: false, isLoading: false });
          setGenericError(true);
          setFlipFlopClear((prev) => !prev);
          return;
        }

        setFlipFlopClear((prev) => !prev);
        const emailVerified = sessionStorage.getItem('emailVerified');
        const { state } = location;
        const historyState = state as HistoryState;

        if (!emailVerified || emailVerified === 'false') {
          sessionStorage.setItem('phoneVerified', 'true');
          history.push('/validar/novidades', { ...historyState, pushEmail: true });
          return;
        }

        window.location.href = `${window.location.origin}/home?upd=${new Date().getTime()}`;
      });
      setIsLoadingAnimation({ isSuccess: !!resp.success, isLoading: false });
    } catch (e) {
      setIsLoadingAnimation({ isSuccess: false, isLoading: false });
      setGenericError(true);
      setFlipFlopClear((prev) => !prev);
    }
  };

  const handleOnChangeToken = (e: React.ChangeEvent) => {
    const element = e.currentTarget as HTMLInputElement;
    setErrorToken('');
    setSmsToken(element.value);
  };

  const goToMyAccount = async () => {
    window.location.replace('/restrito/minha-conta/alterar-dados');
  };

  const handleOnClose = async () => {
    const { state } = location;
    const historyState = state as HistoryState;
    setPhoneIsOpen(false);
    setTimeout(() => {
      history.push('/validar/novidades', { ...historyState, pushEmail: false });
    }, 400);
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_NAME);
    if (token) {
      Config.saveTokenAuthUser({ accessToken: token });
    }
    const { state } = location;
    const historyState = state as HistoryState;

    if (historyState && historyState.maskedPhone && historyState.phoneNumber) {
      const { maskedPhone, phoneNumber } = historyState;
      setPhoneMasked(maskedPhone);
      setPhone(phoneNumber);
    } else {
      const emailVerified = sessionStorage.getItem('emailVerified');
      if (!emailVerified || emailVerified === 'false') {
        history.push('/validar/novidades', { pushEmail: true, maskedEmail: historyState.maskedEmail });
        return;
      }
      history.push('/validar/novidades');
    }
  }, []);

  useEffect(() => {
    if (phoneIsOpen === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-celular', 3);
    }
  }, [phoneIsOpen]);

  useEffect(() => {
    if (insertPhoneTokenIsOpen === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-celular/codigo', 3);
    }
  }, [insertPhoneTokenIsOpen]);

  useEffect(() => {
    if (isLoadingAnimation.isSuccess === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-celular/sucesso', 3);
    }
  }, [isLoadingAnimation]);

  useEffect(() => {
    if (genericError === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-celular/erro-sistema', 3);
    }
  }, [genericError]);

  useEffect(() => {
    if (errorToken.length > 0) {
      loginDataLayer.triggerUserErrorEvent('Valor invalido', errorToken);
    }
  }, [errorToken]);

  const handleChangeCaptcha = (captcha: string | null) => {
    if (!captcha) return;
    setIsValidRecaptcha(true);
    setRecaptchaValue(captcha);
  };

  const handleCaptchaError = () => {
    setIsValidRecaptcha(false);
    setRecaptchaValue('');
  };

  return (
    <>
      <DefaultMsgPage
        traductionKey="confirm_your_phone"
        traductionContentParams={{ phone: phoneMasked }}
        onPrimary={handleToggleInsertPhoneToken}
        onSecondary={handleOnClose}
        image="profile"
      />
      <SmlsModal className="smls-modal-validate__email" isOpen={phoneIsOpen} type="right" defaultHeader={false}>
        <ModalHeader>
          <button
            id="btn_close"
            type="button"
            className="smls-btn-close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={handleOnClose}
          >
            <i className="material-icons">&#xe5cd;</i>
          </button>
        </ModalHeader>
        <ModalBody>
          <div className="smls-modal-header">
            <h1>{t('confirm_your_phone')}</h1>
          </div>
          <div className="smls-modal-text">
            <p>
              <Trans
                t={t}
                i18nKey="confirm_your_phone_message"
                values={{ phone: phoneMasked }}
                components={{ bold: <b /> }}
              />
            </p>
          </div>
          {hasToEnableRecaptcha && (
            <div className="recaptcha">
              <ReCAPTCHA
                sitekey={getConstants('recaptcha').recaptcha.siteKey}
                onChange={handleChangeCaptcha}
                onErrored={handleCaptchaError}
                onExpired={handleCaptchaError}
              />
            </div>
          )}
          <SmlsButton
            text={t('receive_code')}
            disabled={hasToEnableRecaptcha && !isValidRecaptcha}
            onClick={handleToggleInsertPhoneToken}
          />
          <div className="bottom-buttons">
            <span>{t('not_this_number')}</span>
            <SmlsButton color="hyperlink" text={t('update_phone')} onClick={goToMyAccount} />
          </div>
        </ModalBody>
      </SmlsModal>

      <SmlsModal
        className="smls-modal-validate__home"
        isOpen={insertPhoneTokenIsOpen}
        type="right"
        defaultHeader={false}
      >
        <ModalHeader className="left">
          <button
            id="btn_close"
            type="button"
            className="smls-btn-close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              setFlipFlopClear((prev) => !prev);
              setInsertPhoneToken(false);
            }}
          >
            <i className="material-icons">&#xe314;</i>
          </button>
        </ModalHeader>
        <ModalBody>
          <div className="smls-modal-header">
            <h1>{t('enter_validation_code')}</h1>
          </div>
          <div className="smls-modal-text">
            <p>
              <Trans
                t={t}
                i18nKey="enter_validation_code_content"
                values={{ phone: phoneMasked }}
                components={{ bold: <b /> }}
              />
            </p>
          </div>
          <SmlsInputToken
            className="smls-modal-token-input"
            maxLength={6}
            errorMessage={errorToken}
            onChange={handleOnChangeToken}
            flipFlopClear={flipFlopClear}
          />
          {!expiredToken && <p>{t('resent_code_timer', { time: `${formatTime(timeLeft)}` })}</p>}
          {!errorToken && smsToken.length >= 6 && <SmlsButton text={t('continue')} onClick={handleValidateToken} />}
          {expiredToken && hasToEnableRecaptcha && (
            <div className="recaptcha">
              <ReCAPTCHA
                sitekey={getConstants('recaptcha').recaptcha.siteKey}
                onChange={handleChangeCaptcha}
                onErrored={handleCaptchaError}
                onExpired={handleCaptchaError}
              />
            </div>
          )}
          {expiredToken && (
            <SmlsButton
              color="hyperlink"
              text={t('resend_code')}
              disabled={hasToEnableRecaptcha && !isValidRecaptcha}
              onClick={handleToggleInsertPhoneToken}
            />
          )}
        </ModalBody>
      </SmlsModal>

      <SmlsModal className="smls-modal-validate__home" isOpen={genericError} type="right" defaultHeader={false}>
        <ModalHeader>
          <button
            id="btn_close"
            type="button"
            className="smls-btn-close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={() => {
              setPhoneIsOpen(true);
              setInsertPhoneToken(false);
              setGenericError(false);
            }}
          >
            <i className="material-icons">&#xe5cd;</i>
          </button>
        </ModalHeader>
        <ModalBody>
          <div className="smls-modal-header">
            <h1>{t('generic_error_header')}</h1>
          </div>
          <div className="smls-modal-text">
            <p>{t('generic_error_content', { phone: phoneMasked })}</p>
          </div>
          <SmlsButton
            text={t('try_again')}
            onClick={() => {
              setPhoneIsOpen(true);
              setInsertPhoneToken(false);
              setGenericError(false);
              setIsValidRecaptcha(false);
              setRecaptchaValue('');
            }}
          />
        </ModalBody>
      </SmlsModal>
      <SmlsAnimation
        currentState={isLoadingAnimation}
        onExit={onFinishLoading}
        message={animationMessage}
        typeModal="right"
        finishMode="yes"
      />
    </>
  );
};

export default Phone;
